/**
 * Canonical navigation storage for Pixie Dust animation
 * Uses sessionStorage with TTL to ensure refresh-safe navigation
 * 
 * This module provides a single source of truth for storing the next destination
 * before navigating to the pixie dust animation page.
 */

const STORAGE_KEY = 'pixieDustNext';
const TTL_MS = 2 * 60 * 1000; // 2 minutes TTL

/**
 * Set the next destination path for pixie dust navigation
 * @param {string} path - The destination path (e.g., '/agent/itinerary/view/123')
 */
export const setPixieNext = (path) => {
  if (!path || typeof path !== 'string' || path.trim().length === 0) {
    console.warn('[pixieDustNav] Invalid path provided to setPixieNext:', path);
    return;
  }

  try {
    const data = {
      path: path.trim(),
      createdAt: Date.now()
    };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch (e) {
    console.warn('[pixieDustNav] Failed to store next path in sessionStorage:', e);
  }
};

/**
 * Get the next destination path, if valid and not expired
 * @returns {string|null} The destination path or null if missing/expired/invalid
 */
export const getPixieNext = () => {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (!stored) {
      return null;
    }

    const data = JSON.parse(stored);
    
    // Validate structure
    if (!data || typeof data !== 'object' || !data.path || !data.createdAt) {
      console.warn('[pixieDustNav] Invalid stored data structure, clearing');
      clearPixieNext();
      return null;
    }

    // Check TTL
    const age = Date.now() - data.createdAt;
    if (age > TTL_MS) {
      console.warn('[pixieDustNav] Stored path expired, clearing');
      clearPixieNext();
      return null;
    }

    // Validate path format
    if (typeof data.path !== 'string' || data.path.trim().length === 0) {
      console.warn('[pixieDustNav] Invalid path in stored data, clearing');
      clearPixieNext();
      return null;
    }

    return data.path.trim();
  } catch (e) {
    console.warn('[pixieDustNav] Failed to read next path from sessionStorage:', e);
    // Clear corrupted data
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch (clearError) {
      // Ignore clear errors
    }
    return null;
  }
};

/**
 * Clear the stored next destination
 */
export const clearPixieNext = () => {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    // Ignore errors when clearing
  }
};
